/**
 * The frame: a navbar across the top, a sidebar built from the signed-in
 * user's permissions, and the page.
 *
 * Navbar — the menu button and the brand on the left; settings (theme) and
 * the profile menu (name, Edit profile, Sign out) on the right.
 *
 * Sidebar — on wide screens the menu button collapses it to icons (a tooltip
 * names each) and back; the choice is remembered. Below 1080px it slides in
 * over the page instead and closes on navigation.
 *
 * The page's own title, breadcrumbs and buttons sit in the bar under the
 * navbar (`PageTitleSlot`).
 */

import {
	Link,
	NavLink,
	Outlet,
	matchPath,
	useLocation,
	useNavigate,
} from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CheckIcon from '@mui/icons-material/Check';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {
	Avatar,
	Box,
	Button,
	createSvgIcon,
	Divider,
	Drawer,
	IconButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useAuth } from '../provider/auth';
import { PageTitleSlot } from '../provider/pageTitle';
import { useModule } from '../features/modules/useModule';
import { ROLE_LABEL } from '../shared/roles';
import { useAppDispatch, useAppSelector } from '../store';
import { setTheme, toggleRail, type ThemeChoice } from '../store/uiSlice';

/* The sidebar toggle while the sidebar is open: a panel with its left column
   marked (closed, the button shows the hamburger). Drawn here because the
   installed MUI icon set has no such glyph. */
const SidebarIcon = createSvgIcon(
	<>
		<rect
			x="3"
			y="4"
			width="18"
			height="16"
			rx="3.5"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.7"
		/>
		<path
			d="M9.5 4.5v15"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.7"
		/>
	</>,
	'Sidebar',
);

interface Destination {
	to: string;
	label: string;
	blurb: string;
	icon: ReactNode;
	/** `end`: match this path exactly, not its sub-routes. */
	end?: boolean;
	/** Other path prefixes that belong to this item: inside a client's
	 *  documents or audits, Clients stays lit. */
	alsoActive?: string[];
}

interface Group {
	label: string;
	show: boolean;
	items: Destination[];
}

const THEMES: Array<{ value: ThemeChoice; label: string; icon: ReactNode }> = [
	{
		value: 'light',
		label: 'Light',
		icon: <LightModeOutlinedIcon fontSize="small" />,
	},
	{
		value: 'dark',
		label: 'Dark',
		icon: <DarkModeOutlinedIcon fontSize="small" />,
	},
	{
		value: 'system',
		label: 'System default',
		icon: <SettingsBrightnessOutlinedIcon fontSize="small" />,
	},
];

/**
 * Two letters off the name, for the avatar.
 *
 * First and last word rather than the first two, so "Tushar Kumar Panchal" is
 * TP and not TK — the middle name is the part nobody uses.
 */
function initials(name: string | undefined): string {
	const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '—';
	const first = words[0]![0]!;
	const last = words.length > 1 ? words[words.length - 1]![0]! : '';
	return (first + last).toUpperCase();
}

/* ── Styles (MUI sx) ─────────────────────────────────────────────────────── */

const HIDE_IN_PRINT = { '@media print': { display: 'none' } } as const;

const APPBAR_SX = {
	position: 'sticky',
	top: 0,
	zIndex: 40,
	height: 'var(--appbar-h)',
	boxSizing: 'border-box',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 'var(--s3)',
	p: '0 var(--s4) 0 var(--s3)',
	bgcolor: 'var(--surface)',
	borderBottom: '1px solid var(--line)',
	...HIDE_IN_PRINT,
} as const;

const BRAND_SX = {
	display: 'flex',
	alignItems: 'center',
	gap: 'var(--s2)',
	fontWeight: 700,
	fontSize: 'var(--fs-section)',
	letterSpacing: '-0.01em',
	color: 'var(--ink)',
	textDecoration: 'none',
} as const;

const BRAND_MARK_SX = {
	width: 26,
	height: 26,
	borderRadius: 'var(--r-sm)',
	display: 'grid',
	placeItems: 'center',
	bgcolor: 'var(--rail-mark-a)',
	color: 'var(--ink-on-solid)',
	fontSize: 'var(--fs-body)',
	fontWeight: 700,
	lineHeight: 1,
} as const;

/** The sidebar panel: docked under the navbar on wide screens; MUI places
 *  the slide-in version itself. */
const railPaperSx = (collapsed: boolean, overlays: boolean) =>
	({
		width: collapsed ? 'var(--rail-w-collapsed)' : 'var(--rail-w)',
		bgcolor: 'var(--rail-bg)',
		color: 'var(--rail-ink)',
		display: 'flex',
		flexDirection: 'column',
		border: 'none',
		borderRight: '1px solid var(--rail-line)',
		overflowX: 'hidden',
		zIndex: 30,
		transition: 'width var(--dur) var(--ease)',
		...(overlays
			? { boxShadow: 'var(--e4)' }
			: {
					position: 'sticky',
					top: 'var(--appbar-h)',
					height: 'calc(100vh - var(--appbar-h))',
				}),
		'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
		...HIDE_IN_PRINT,
	}) as const;

const railNavSx = (collapsed: boolean) =>
	({
		p: collapsed ? 'var(--s2)' : 'var(--s3) var(--s2)',
		flex: 1,
		overflowY: 'auto',
		overflowX: 'hidden',
	}) as const;

/** A group's name; collapsed, a thin rule between groups (none above the first). */
const railLabelSx = (collapsed: boolean, first: boolean) =>
	collapsed
		? first
			? { display: 'none' }
			: {
					height: 0,
					p: 0,
					m: 'var(--s2)',
					borderTop: '1px solid var(--rail-line)',
				}
		: {
				fontSize: 'var(--fs-small)',
				fontWeight: 600,
				color: 'var(--rail-ink-2)',
				p: 'var(--s3) var(--s2) var(--s1)',
			};

const railItemSx = (collapsed: boolean) =>
	({
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: collapsed ? 'center' : 'flex-start',
		gap: 'var(--s3)',
		p: collapsed ? 'var(--s2) 0' : 'var(--s2) var(--s3)',
		mb: '1px',
		minHeight: 36,
		borderRadius: 'var(--r-sm)',
		color: 'var(--rail-ink-2)',
		fontSize: 'var(--fs-body)',
		fontWeight: 500,
		textDecoration: 'none',
		transition:
			'background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease)',
		'&:hover': { bgcolor: 'var(--rail-hover)', color: 'var(--rail-ink)' },
		'&:focus-visible': {
			outline: '2px solid var(--accent)',
			outlineOffset: '-2px',
		},
		'&[aria-current="page"]': {
			bgcolor: 'var(--rail-active)',
			color: 'var(--rail-active-ink)',
			fontWeight: 600,
			/* The bar at the left edge of the current page's item. */
			'&::before': {
				content: '""',
				position: 'absolute',
				left: '-2px',
				top: '50%',
				transform: 'translateY(-50%)',
				width: 3,
				height: 18,
				borderRadius: '0 var(--r-full) var(--r-full) 0',
				bgcolor: 'var(--accent-solid)',
			},
		},
	}) as const;

const RAIL_ICON_SX = {
	display: 'grid',
	placeItems: 'center',
	flex: '0 0 20px',
	'& svg': { fontSize: 20 },
} as const;

const signOutSx = (collapsed: boolean) =>
	({
		justifyContent: collapsed ? 'center' : 'flex-start',
		gap: 'var(--s1)',
		minWidth: 0,
		p: collapsed ? 'var(--s2) 0' : 'var(--s2) var(--s3)',
		color: 'var(--rail-ink-2)',
		fontWeight: 500,
		'&:hover': { bgcolor: 'var(--rail-hover)', color: 'var(--danger-ink)' },
	}) as const;

/** The page bar under the navbar: breadcrumbs, then the title and its status
 *  on one line, the page's buttons on the right. */
const TOPBAR_SX = {
	position: 'sticky',
	top: 'var(--appbar-h)',
	zIndex: 20,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	gap: 'var(--s3)',
	p: 'var(--s2) var(--s5)',
	bgcolor: 'var(--surface)',
	borderBottom: '1px solid var(--line)',
	'@media (max-width: 620px)': { p: 'var(--s2) var(--s3)' },
	'& .MuiButton-root:not(.MuiButton-sizeSmall)': { height: 32 },
	/* Plain controls a page puts in its bar stay compact (`&&` outranks the
     global input rule in base.css). */
	'&& select, && input, && .ctl': {
		minHeight: 'var(--ctl-h-sm)',
		fontSize: 'var(--fs-body)',
		width: 'auto',
		flex: '0 1 230px',
		minWidth: 0,
		maxWidth: 340,
	},
	...HIDE_IN_PRINT,
} as const;

const TITLE_SLOT_SX = {
	minWidth: 0,
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	columnGap: 'var(--s3)',
	rowGap: 0,
	'& > .MuiBreadcrumbs-root': { flexBasis: '100%' },
} as const;

const ACTS_SLOT_SX = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: 'var(--s2)',
	minWidth: 0,
	flex: '0 1 auto',
} as const;

export default function Shell() {
	const { user, permissions, signOut } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	/* Every link out of the frame keeps the current module. */
	const { link: inModule } = useModule();
	const isActive = (destination: Destination) =>
		Boolean(
			matchPath(
				{ path: destination.to, end: destination.end ?? false },
				location.pathname,
			),
		) ||
		(destination.alsoActive ?? []).some(
			(prefix) =>
				location.pathname === prefix ||
				location.pathname.startsWith(`${prefix}/`),
		);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const collapsedPref = useAppSelector((state) => state.ui.railCollapsed);
	const themeChoice = useAppSelector((state) => state.ui.theme);
	/* Matches the documented 1080px rail-overlay threshold (`breakpoints.ts`'s
     `lg`) — the rail is a permanent column at and above it, a temporary
     overlay drawer below it. */
	const railOverlays = useMediaQuery(theme.breakpoints.down('lg'));
	const [railOpen, setRailOpen] = useState(false);
	/* Icons only — wide screens only; the slide-in rail is always full. */
	const collapsed = !railOverlays && collapsedPref;
	/* Whether the sidebar is showing in full: the slide-in drawer's own state
     on narrow screens, the expanded rail on wide ones. */
	const railShown = railOverlays ? railOpen : !collapsed;
	/* The widths live in tokens.css (`--rail-w`, `--rail-w-collapsed`): the
     panel (`.rail`) and the space kept for it here must be the same number. */
	const railWidth = collapsed ? 'var(--rail-w-collapsed)' : 'var(--rail-w)';
	const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
		null,
	);
	const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(
		null,
	);

	/* The rail overlays the page below 1080px, so it has to close on its own
     when navigation happens — otherwise the panel stays over the screen the
     reader just asked for. Keyed on the path, so browser back and forward
     close it too, not only a click on the rail's own links. */
	useEffect(() => setRailOpen(false), [location.pathname]);

	/* A page change is instant, so it can pass unnoticed: the new page fades
     in from a slight blur. Played on the element rather than by remounting
     it, so a page that only changes tab keeps its state; a query change
     (a filter, a selected group) does not play it. */
	const main = useRef<HTMLElement>(null);
	const firstPath = useRef(true);
	useEffect(() => {
		if (firstPath.current) {
			firstPath.current = false;
			return;
		}
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
			return;
		main.current?.animate?.(
			[
				{
					opacity: 0.35,
					filter: 'blur(3px)',
					transform: 'translateY(4px)',
				},
				{ opacity: 1, filter: 'blur(0)', transform: 'none' },
			],
			{ duration: 260, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
		);
		/* The page's title renders into the page bar, outside `main`. */
		topbar.current?.animate?.([{ opacity: 0.35 }, { opacity: 1 }], {
			duration: 260,
			easing: 'ease-out',
		});
	}, [location.pathname]);

	/* Publish how much of the screen the fixed chrome takes (navbar + page
     bar) so a page can size itself under it. Measured, not assumed: the page
     bar wraps with a long title and several actions. */
	const appbar = useRef<HTMLElement>(null);
	const topbar = useRef<HTMLElement>(null);
	useEffect(() => {
		const bar = appbar.current;
		const page = topbar.current;
		if (!bar || !page) return;
		const publish = () =>
			document.documentElement.style.setProperty(
				'--topbar-h',
				`${bar.offsetHeight + page.offsetHeight}px`,
			);
		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(bar);
		observer.observe(page);
		return () => observer.disconnect();
	}, []);

	const roleWord = user ? ROLE_LABEL[user.role] : '';

	/* Each group shows only to the role that works there; a destination
     nobody in this role can use is absent, not disabled. */
	const groups: Group[] = [
		{
			label: 'Work',
			show: Boolean(
				permissions?.accessIntake || permissions?.accessAudits,
			),
			items: [
				// {
				//   to: '/dashboard',
				//   label: 'Dashboard',
				//   blurb: 'Where to start today',
				//   icon: <SpaceDashboardOutlinedIcon />,
				// },
				{
					to: '/clients',
					label: 'Clients',
					blurb: "Each client's documents and audits",
					icon: <BusinessOutlinedIcon />,
					/* One entry for both halves: it stays lit on a client's documents
             (/intake/...) and audits (/audits/...) as well as /clients/... */
					alsoActive: ['/intake', '/audits'],
				},
				// {
				//   to: '/audits',
				//   label: 'Audits',
				//   blurb: 'Every audit, current and past',
				//   icon: <FactCheckOutlinedIcon />,
				//   end: true,
				// },
				// ...(permissions?.generateReports
				//   ? [
				//       {
				//         to: '/approvals',
				//         label: 'Approvals',
				//         blurb: 'Report versions waiting for sign-off',
				//         icon: <TaskAltOutlinedIcon />,
				//       },
				//     ]
				//   : []),
			],
		},
		{
			label: 'Firm',
			show: true,
			items: [
				// {
				//   to: '/activity',
				//   label: 'Activity log',
				//   blurb: 'Who did what, and when',
				//   icon: <HistoryOutlinedIcon />,
				// },
				...(permissions?.manageUsers
					? [
							{
								to: '/firm/configuration',
								label: 'Configuration',
								blurb: 'Analyses, column names and classification rules',
								icon: <TuneOutlinedIcon />,
							},
							{
								to: '/administration',
								label: 'People',
								blurb: 'Users, roles and invitations',
								icon: <GroupOutlinedIcon />,
							},
						]
					: []),
			],
		},
	];

	const visible = groups.filter(
		(group) => group.show && group.items.length > 0,
	);

	const doSignOut = () => {
		void signOut().then(() => navigate('/sign-in', { replace: true }));
	};

	const signOutButton = (
		<Button
			fullWidth
			variant="text"
			color="inherit"
			onClick={doSignOut}
			aria-label="Sign out"
			startIcon={collapsed ? undefined : <LogoutIcon />}
			sx={signOutSx(collapsed)}
		>
			{collapsed ? <LogoutIcon /> : 'Sign out'}
		</Button>
	);

	const railContent = (
		<>
			<Box sx={railNavSx(collapsed)}>
				{visible.map((group, index) => (
					<Box key={`${group.label}-${index}`}>
						{/* Collapsed, a group's name becomes a thin rule between groups. */}
						<Box
							aria-hidden={collapsed || undefined}
							sx={railLabelSx(collapsed, index === 0)}
						>
							{collapsed ? null : group.label}
						</Box>
						{group.items.map((destination) => {
							const link = (
								<Box
									component={Link}
									key={destination.to}
									to={inModule(destination.to)}
									aria-current={
										isActive(destination)
											? 'page'
											: undefined
									}
									title={
										collapsed
											? undefined
											: destination.blurb
									}
									aria-label={
										collapsed
											? destination.label
											: undefined
									}
									sx={railItemSx(collapsed)}
								>
									<Box
										component="span"
										aria-hidden="true"
										sx={RAIL_ICON_SX}
									>
										{destination.icon}
									</Box>
									{!collapsed && (
										<Typography
											component="span"
											sx={{
												flex: 1,
												minWidth: 0,
												font: 'inherit',
											}}
										>
											{destination.label}
										</Typography>
									)}
								</Box>
							);
							return collapsed ? (
								<Tooltip
									key={destination.to}
									placement="right"
									title={destination.label}
								>
									{link}
								</Tooltip>
							) : (
								link
							);
						})}
					</Box>
				))}
			</Box>
			{/* Sign out, always at the foot of the sidebar. */}
			<Box
				sx={{ p: 'var(--s2)', borderTop: '1px solid var(--rail-line)' }}
			>
				{collapsed ? (
					<Tooltip title="Sign out" placement="right">
						{signOutButton}
					</Tooltip>
				) : (
					signOutButton
				)}
			</Box>
		</>
	);

	const closeMenus = () => {
		setSettingsAnchor(null);
		setProfileAnchor(null);
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100vh',
			}}
		>
			<a className="skip-link" href="#main">
				Skip to content
			</a>

			<Box component="header" ref={appbar} sx={APPBAR_SX}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 'var(--s2)',
						minWidth: 0,
					}}
				>
					<Tooltip
						title={railShown ? 'Close sidebar' : 'Open sidebar'}
					>
						<IconButton
							onClick={() =>
								railOverlays
									? setRailOpen((open) => !open)
									: dispatch(toggleRail())
							}
							aria-label={
								railShown ? 'Close sidebar' : 'Open sidebar'
							}
							aria-expanded={railShown}
							size="small"
						>
							{railShown ? <SidebarIcon /> : <MenuIcon />}
						</IconButton>
					</Tooltip>
					<Box
						component={NavLink}
						to={inModule('/clients')}
						sx={BRAND_SX}
					>
						<Box
							component="span"
							aria-hidden="true"
							sx={BRAND_MARK_SX}
						>
							R
						</Box>
						Reconcilio
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 'var(--s1)',
						minWidth: 0,
					}}
				>
					<Tooltip title="Settings">
						<IconButton
							size="small"
							aria-label="Settings"
							aria-haspopup="menu"
							onClick={(event) =>
								setSettingsAnchor(event.currentTarget)
							}
						>
							<SettingsOutlinedIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Your profile">
						<IconButton
							size="small"
							aria-label="Your profile"
							aria-haspopup="menu"
							onClick={(event) =>
								setProfileAnchor(event.currentTarget)
							}
						>
							<Avatar
								sx={{
									width: 30,
									height: 30,
									fontSize: 'var(--fs-small)',
									fontWeight: 600,
									bgcolor: 'var(--accent-deep)',
								}}
							>
								{initials(user?.name)}
							</Avatar>
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Menu
				anchorEl={settingsAnchor}
				open={Boolean(settingsAnchor)}
				onClose={closeMenus}
				/* Centred under the ⚙ button, just as wide as its options. */
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
				slotProps={{ paper: { sx: { minWidth: 168, mt: 0.5 } } }}
			>
				<ListSubheader
					sx={{ lineHeight: '32px', fontSize: 'var(--fs-small)' }}
				>
					Theme
				</ListSubheader>
				{THEMES.map((option) => (
					<MenuItem
						key={option.value}
						selected={themeChoice === option.value}
						onClick={() => {
							dispatch(setTheme(option.value));
							closeMenus();
						}}
					>
						<ListItemIcon sx={{ minWidth: '30px !important' }}>
							{option.icon}
						</ListItemIcon>
						<ListItemText>{option.label}</ListItemText>
						{themeChoice === option.value && (
							<CheckIcon
								fontSize="small"
								sx={{ ml: 1, color: 'var(--accent)' }}
							/>
						)}
					</MenuItem>
				))}
			</Menu>

			<Menu
				anchorEl={profileAnchor}
				open={Boolean(profileAnchor)}
				onClose={closeMenus}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
			>
				<Box sx={{ px: 2, py: 1.25 }}>
					<Typography
						sx={{ fontWeight: 600, fontSize: 'var(--fs-section)' }}
					>
						{user?.name}
					</Typography>
					<Typography
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{user?.email}
					</Typography>
					{/* The role is stated, not switchable. */}
					<Typography
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{roleWord}
						{user?.firmName ? ` · ${user.firmName}` : ''}
					</Typography>
				</Box>
				<Divider />
				<MenuItem
					onClick={() => {
						closeMenus();
						navigate(inModule('/account'));
					}}
				>
					<ListItemIcon>
						<PersonOutlineIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Edit profile</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						closeMenus();
						doSignOut();
					}}
				>
					<ListItemIcon>
						<LogoutIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Sign out</ListItemText>
				</MenuItem>
			</Menu>

			<Box
				sx={{
					display: 'flex',
					flex: 1,
					minHeight: 0,
					'@media print': { display: 'block' },
				}}
			>
				<Drawer
					variant={railOverlays ? 'temporary' : 'permanent'}
					open={railOverlays ? railOpen : true}
					onClose={() => setRailOpen(false)}
					ModalProps={{ keepMounted: true }}
					PaperProps={{ sx: railPaperSx(collapsed, railOverlays) }}
					sx={{
						width: railOverlays ? 0 : railWidth,
						flexShrink: 0,
						transition: 'width var(--dur) var(--ease)',
					}}
				>
					{railContent}
				</Drawer>

				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Box component="header" ref={topbar} sx={TOPBAR_SX}>
						{/* The page renders its own name into these slots — half the
                titles are data ("Eris Pharma", a file name), so only the page
                can state them. */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 'var(--s3)',
								minWidth: 0,
								flex: '1 1 260px',
							}}
						>
							<PageTitleSlot slot="title" sx={TITLE_SLOT_SX} />
						</Box>
						<PageTitleSlot slot="acts" sx={ACTS_SLOT_SX} />
					</Box>

					{/* `.wrap`, not `.page`. There is no `.page` rule in any stylesheet —
              the previous markup used one and the main column was unstyled. */}
					<Box component="main" id="main" className="wrap" ref={main}>
						<Outlet />
					</Box>
				</Box>
			</Box>
		</Box>
	);
}
